/**
 * The /looker filter-bar pill for the open history download (replaces the
 * retired "Backfill" app box, 2026-10-13). Nothing open, loading, or the
 * all-workspaces scope → nothing. A failed read says so: an empty pill would
 * read as "no backfill". Warning colour only when a stuck or failed count is
 * non-zero; a zero is never coloured.
 *
 * @module components/looker/BackfillStatusPill
 */
import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { platformLabel } from '@/utils/platformLabel';
import { backfillTotal, type BackfillAccount, type BackfillEta, type BackfillPlatform } from '@/services/looker/lookerAppData';
import { etaText } from '@/services/looker/backfillEtaText';
import type { BackfillState } from '@/hooks/useLookerBackfill';
import { POPOVER_PANEL_CLASS, useDismiss, usePanelPlacement } from './statusPopover';

const DASH = '—';

const sumOf = (platforms: readonly BackfillPlatform[], pick: (p: BackfillPlatform) => number): number =>
  platforms.reduce((n, p) => n + pick(p), 0);

const Count: React.FC<{ label: string; value: number; alarm?: boolean }> = ({ label, value, alarm = false }) => (
  <span data-count className={`whitespace-nowrap ${alarm && value > 0 ? 'font-semibold text-warning-700' : 'text-neutral-800'}`}>
    {label} <span className="tabular-nums">{value}</span>
  </span>
);

type Counted = Pick<BackfillPlatform, 'done' | 'queued' | 'running' | 'stuck' | 'failed'>;

const Counts: React.FC<{ c: Counted }> = ({ c }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
      <Count label={t('looker.backfillStatus.count.done')} value={c.done} />
      <Count label={t('looker.backfillStatus.count.waiting')} value={c.queued} />
      <Count label={t('looker.backfillStatus.count.running')} value={c.running} />
      <Count label={t('looker.backfillStatus.count.stuck')} value={c.stuck} alarm />
      <Count label={t('looker.backfillStatus.count.failed')} value={c.failed} alarm />
    </div>
  );
};

const Window: React.FC<{ from: string | null; to: string | null }> = ({ from, to }) => {
  const { t } = useTranslation();
  return (
    <p className="text-xs text-neutral-700 tabular-nums">
      <span className="sr-only">{t('looker.backfillStatus.window')} </span>
      {from !== null && to !== null ? `${from} – ${to}` : DASH}
    </p>
  );
};

const AccountRow: React.FC<{ a: BackfillAccount }> = ({ a }) => {
  const { t } = useTranslation();
  return (
    <li data-account className="py-1.5 pl-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <p className="min-w-0 break-words text-xs font-medium text-neutral-800">{a.accountId === null ? t('looker.backfillStatus.unassigned') : a.name ?? a.accountId.slice(0, 8)}</p>
        <Window from={a.from} to={a.to} />
      </div>
      <p className="text-xs text-neutral-700 tabular-nums">
        {a.availableFrom === null || a.availableTo === null
          ? t('looker.backfillStatus.notAvailableYet')
          : a.availableTo === a.to
            ? t('looker.backfillStatus.availableFrom', { date: a.availableFrom })
            : t('looker.backfillStatus.availableRange', { from: a.availableFrom, to: a.availableTo })}
      </p>
      <Counts c={a} />
    </li>
  );
};

const PlatformRow: React.FC<{ p: BackfillPlatform }> = ({ p }) => (
  <li data-platform={p.platform} className="py-2">
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
      <p className="font-semibold text-neutral-900">{platformLabel(p.platform)}</p>
      <Window from={p.from} to={p.to} />
    </div>
    <Counts c={p} />
    {p.accounts.length > 0 && (
      <ul className="mt-1 divide-y divide-neutral-100 border-l border-neutral-200">
        {p.accounts.map((a) => <AccountRow key={a.accountId ?? 'unassigned'} a={a} />)}
      </ul>
    )}
  </li>
);

/** One line for the whole window. The worker's queue is shared: counted when it could be read, else a lower bound with a warning. */
const EtaLine: React.FC<{ eta: BackfillEta }> = ({ eta }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-2 border-t border-neutral-100 pt-2 text-xs">
      <p className="font-medium text-neutral-800">{`${t('looker.backfillStatus.eta.label')}: ${etaText(t, eta)}`}</p>
      {eta.lowerBound && <p className="text-neutral-700">{t('looker.backfillStatus.eta.note')}</p>}
    </div>
  );
};

/** The pill's one-line summary, in the viewer's language. */
const useSummary = (platforms: readonly BackfillPlatform[]): string => {
  const { t } = useTranslation();
  const label = t('looker.backfillStatus.label');
  const done = sumOf(platforms, (p) => p.done);
  const total = sumOf(platforms, backfillTotal);
  const head = platforms.length === 1
    ? `⟳ ${label} · ${platformLabel(platforms[0].platform)} ${done}/${total}`
    : `⟳ ${label} ${t('looker.backfillStatus.channels', { n: platforms.length })} · ${t('looker.backfillStatus.done')} ${done}/${total}`;
  const stuck = sumOf(platforms, (p) => p.stuck);
  const failed = sumOf(platforms, (p) => p.failed);
  return [
    head,
    ...(stuck > 0 ? [t('looker.backfillStatus.stuck', { n: stuck })] : []),
    ...(failed > 0 ? [t('looker.backfillStatus.failed', { n: failed })] : []),
  ].join(' · ');
};

const OpenPill: React.FC<{ platforms: BackfillPlatform[]; eta: BackfillEta }> = ({ platforms, eta }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const close = React.useCallback(() => setOpen(false), []);
  useDismiss(open, root, close);
  const placement = usePanelPlacement(open, root);
  const summary = useSummary(platforms);
  const alarm = platforms.some((p) => p.stuck > 0 || p.failed > 0);
  const tone = alarm ? 'border-warning-700 text-warning-700' : 'border-neutral-300 text-neutral-800';

  return (
    <div ref={root} className="relative min-w-0 max-w-full">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        title={t('looker.backfillStatus.details')}
        onClick={() => setOpen((v) => !v)}
        className={`min-w-0 max-w-full whitespace-normal break-words rounded-full border bg-white px-2.5 py-1 text-left text-xs font-medium tabular-nums hover:bg-neutral-50 ${tone}`}
      >
        {summary}
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label={t('looker.backfillStatus.heading')}
          className={POPOVER_PANEL_CLASS}
          style={placement ?? undefined}
        >
          <p className="text-xs font-semibold text-neutral-800">{t('looker.backfillStatus.heading')}</p>
          <ul className="max-h-80 divide-y divide-neutral-100 overflow-y-auto">
            {platforms.map((p) => <PlatformRow key={p.platform} p={p} />)}
          </ul>
          <EtaLine eta={eta} />
        </div>
      )}
    </div>
  );
};

export const BackfillStatusPill: React.FC<{ state: BackfillState }> = ({ state }) => {
  const { t } = useTranslation();
  if (state.status === 'failed') {
    return <span className="text-xs text-neutral-700">{t('looker.backfillStatus.couldntLoad')}</span>;
  }
  if (state.status !== 'ready' || state.platforms.length === 0) return null;
  return <OpenPill platforms={state.platforms} eta={state.eta} />;
};
